<template>
  <div class="transaction-list-view">
    <!-- Header -->
    <div class="flex justify-between items-center mb-3">
      <h1 class="text-2xl font-bold">{{ pageTitle }}</h1>
      <div class="flex ml-auto gap-2">
        <!-- SpeedDial for creating new transactions -->
        <SpeedDial
          :model="speedDialItems"
          direction="left"
          :transitionDelay="80"
          :style="{ position: 'relative' }"
          pt:menuitem="m-2"
        >
          <template #button="{ toggleCallback }">
            <Button icon="pi pi-plus" @click="toggleCallback" />
          </template>
          <template #item="{ item, toggleCallback }">
            <div
              class="flex flex-col items-center justify-between gap-2 p-2 border rounded border-surface-200 dark:border-surface-700 w-20 cursor-pointer"
              @click="
                (event) => {
                  item.action()
                  toggleCallback(event)
                }
              "
            >
              <span :class="item.icon" />
              <span>
                {{ item.label }}
              </span>
            </div>
          </template>
        </SpeedDial>
      </div>
    </div>

    <!-- Tabs -->
    <div class="">
      <TabView v-model:activeIndex="activeTabIndex">
        <TabPanel value="0">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Open</span>
              <Badge :value="openCount" severity="info" />
            </div>
          </template>
          <div class="p-4 rounded-lg border">
            <div class="flex gap-4">
              <Dropdown
                v-model="transactionType"
                :options="transactionTypeOptions"
                option-label="label"
                option-value="value"
                placeholder="All Types"
                class="w-48"
                @change="onFilterChange"
              />
              <InputText
                v-model="search"
                placeholder="Search by party name..."
                class="flex-1"
                @input="onSearchChange"
              />
              <Button
                v-if="canExport"
                icon="pi pi-download"
                size="small"
                outlined
                v-tooltip="'Export CSV'"
                @click="handleExport"
                :disabled="transactions.length === 0"
              />
            </div>
          </div>
        </TabPanel>
        <TabPanel value="1">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Complete</span>
              <Badge :value="completeCount" severity="success" />
            </div>
          </template>
          <div class="p-4 rounded-lg border">
            <div class="flex gap-4">
              <Dropdown
                v-model="transactionType"
                :options="transactionTypeOptions"
                option-label="label"
                option-value="value"
                placeholder="All Types"
                class="w-48"
                @change="onFilterChange"
              />
              <InputText
                v-model="search"
                placeholder="Search by party name..."
                class="flex-1"
                @input="onSearchChange"
              />
              <Button
                v-if="canExport"
                icon="pi pi-download"
                size="small"
                outlined
                v-tooltip="'Export CSV'"
                @click="handleExport"
                :disabled="transactions.length === 0"
              />
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <ProgressSpinner />
    </div>

    <!-- Transactions List -->
    <div v-else class="space-y-4">
      <TransactionCard
        v-for="transaction in transactions"
        :key="transaction.id"
        :transaction="transaction"
        @view="viewTransaction"
        @edit="editTransaction"
        @delete="handleDeleteTransaction"
        class="my-1"
      />
    </div>

    <!-- Empty State -->
    <div v-if="!loading && transactions.length === 0" class="text-center py-8">
      <i class="pi pi-inbox text-4xl text-gray-400 mb-4"></i>
      <h3 class="text-lg font-medium text-gray-600 mb-2">No transactions found</h3>
      <p class="text-gray-500">Try adjusting your filters or create a new transaction.</p>
    </div>

    <!-- Load More Button -->
    <div v-if="!loading && transactions.length > 0 && hasMore" class="text-center mt-6">
      <Button label="Load More" icon="pi pi-refresh" outlined @click="loadMore" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useTransactionStore } from '@/stores/v2/transaction'
import { useAuthStore } from '@/stores/authStore'
import { createTransaction } from '@/factories/transactionFactory'
import { exportTransactionsToCSV } from '@/utils/csvExport'
import type { TransactionRecord } from '@/types/Transaction'

// Components
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import SpeedDial from 'primevue/speeddial'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import TransactionCard from '@/components/transaction/TransactionCard.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const transactionStore = useTransactionStore()
const authStore = useAuthStore()

// State
const transactions = ref<TransactionRecord[]>([])
const loading = ref(false)
const search = ref('')
const transactionType = ref<'purchase' | 'sale' | 'transfer' | 'adjustment' | undefined>()
const pageSize = ref(20)
const hasMore = ref(true)

// Page title based on transaction type filter
const pageTitle = computed(() => {
  if (transactionType.value === 'purchase') return 'Purchases'
  if (transactionType.value === 'sale') return 'Sales'
  return 'Transactions'
})

// Check if user can export (SaaS admin or tenant admin)
const canExport = computed(() => {
  return authStore.isSaasAdmin || authStore.isTenantAdmin
})

// Tab state
const activeTabIndex = ref<number>(0)

// Count state
const openCount = ref(0)
const completeCount = ref(0)

// Debounce search
let searchTimeout: NodeJS.Timeout | null = null

// Function to update counts
async function updateCounts() {
  try {
    // Create separate search instances for each count
    const openSearchInstance = await transactionStore.search()
    const completeSearchInstance = await transactionStore.search()

    // Build open count query
    let openQuery = openSearchInstance.countOpen()
    if (transactionType.value) {
      openQuery = openQuery.byTransactionType(transactionType.value)
    }

    // Build complete count query
    let completeQuery = completeSearchInstance.countCompleted()
    if (transactionType.value) {
      completeQuery = completeQuery.byTransactionType(transactionType.value)
    }

    const openResult = await openQuery.count()
    const completeResult = await completeQuery.count()

    openCount.value = openResult
    completeCount.value = completeResult
  } catch (error) {
    console.error('Error updating counts:', error)
  }
}

// Load transactions based on current tab and search
async function loadTransactions() {
  loading.value = true
  try {
    const searchInstance = await transactionStore.search()
    let searchQuery = searchInstance

    // Apply transaction type filter
    if (transactionType.value) {
      searchQuery = searchQuery.byTransactionType(transactionType.value)
    }

    // Apply tab filter
    const tabIndex = activeTabIndex.value ?? 0
    if (tabIndex === 0) {
      // Open tab - exclude completed
      searchQuery = searchQuery.excludeCompleted()
    } else if (tabIndex === 1) {
      // Complete tab - only completed
      searchQuery = searchQuery.onlyCompleted()
    }

    // Apply search filter
    if (search.value.trim()) {
      const searchTerm = search.value.trim()
      searchQuery = searchQuery.prefixPartyName(searchTerm, pageSize.value).recent()
    } else {
      // Default to recently updated
      searchQuery = searchQuery.recent(pageSize.value)
    }

    const results = await searchQuery.run()
    transactions.value = results
    hasMore.value = transactions.value.length === pageSize.value
  } catch (error) {
    console.error('Error loading transactions:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load transactions',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

// Watch for search changes with debounce
function onSearchChange() {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    loadTransactions()
  }, 300)
}

// Watch for tab changes
watch(activeTabIndex, () => {
  loadTransactions()
})

// Watch for transaction type changes
watch(transactionType, () => {
  loadTransactions()
  updateCounts()
})

// Watch for URL query param changes and update filter
watch(
  () => route.query.type,
  (type) => {
    if (type === 'purchase' || type === 'sale' || type === 'transfer' || type === 'adjustment') {
      transactionType.value = type
    } else {
      transactionType.value = undefined
    }
  },
)

// Filter options
const transactionTypeOptions = [
  { label: 'All Types', value: undefined },
  { label: 'Purchase', value: 'purchase' },
  { label: 'Sale', value: 'sale' },
  { label: 'Transfer', value: 'transfer' },
  { label: 'Adjustment', value: 'adjustment' },
]

// SpeedDial items for creating new transactions
const speedDialItems = [
  {
    label: 'Sale',
    icon: 'pi pi-dollar text-lg',
    action: () => navigateToNewSale(),
  },
  {
    label: 'Purchase',
    icon: 'pi pi-shopping-cart text-lg',
    action: () => navigateToWizardTransaction(),
  },
]

// Initialize
onMounted(async () => {
  // Initialize transaction type from URL param
  const typeParam = route.query.type
  if (
    typeParam === 'purchase' ||
    typeParam === 'sale' ||
    typeParam === 'transfer' ||
    typeParam === 'adjustment'
  ) {
    transactionType.value = typeParam
  } else {
    transactionType.value = undefined
  }

  await updateCounts()
  await nextTick()
  activeTabIndex.value = openCount.value === 0 && completeCount.value > 0 ? 1 : 0
  await loadTransactions()
})

function onFilterChange() {
  loadTransactions()
}

function loadMore() {
  pageSize.value += 20
  loadTransactions()
}

// Transaction methods
async function navigateToNewSale() {
  const newTransaction = createTransaction({ transaction_type: 'sale' })
  const savedTransactionId = await transactionStore.create(newTransaction)
  router.push(`/transaction/${savedTransactionId}/edit`)
}

async function navigateToWizardTransaction() {
  const newTransaction = createTransaction()
  const savedTransactionId = await transactionStore.create(newTransaction)
  router.push(`/transaction/${savedTransactionId}/wizard`)
}

function viewTransaction(transaction: TransactionRecord) {
  if (!transaction?.id) return
  router.push(`/transaction/${transaction.id}`)
}

function editTransaction(transaction: TransactionRecord) {
  if (!transaction?.id) return
  router.push(`/transaction/${transaction.id}/edit`)
}

async function handleDeleteTransaction(transaction: TransactionRecord) {
  if (!transaction?.id) return

  try {
    await transactionStore.remove(transaction.id)
    toast.add({
      severity: 'success',
      summary: 'Transaction Deleted',
      detail: `Transaction ${transaction.transaction_number} has been deleted`,
      life: 3000,
    })
    await loadTransactions()
    await updateCounts()
  } catch (error) {
    console.error('Error deleting transaction:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete transaction',
      life: 3000,
    })
  }
}

// Export transactions to CSV
function handleExport() {
  if (transactions.value.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'No Transactions',
      detail: 'There are no transactions to export',
      life: 3000,
    })
    return
  }

  try {
    // Generate filename based on current filters
    const timestamp = new Date().toISOString().split('T')[0]
    let filename = `transactions-${timestamp}.csv`

    if (transactionType.value) {
      filename = `${transactionType.value}-${timestamp}.csv`
    }

    if (activeTabIndex.value === 0) {
      filename = `open-${filename}`
    } else if (activeTabIndex.value === 1) {
      filename = `complete-${filename}`
    }

    exportTransactionsToCSV(transactions.value, filename)
    toast.add({
      severity: 'success',
      summary: 'Export Successful',
      detail: `Exported ${transactions.value.length} transactions to CSV`,
      life: 3000,
    })
  } catch (error) {
    console.error('Error exporting transactions:', error)
    toast.add({
      severity: 'error',
      summary: 'Export Failed',
      detail: 'Failed to export transactions',
      life: 3000,
    })
  }
}
</script>

<style scoped>
.transaction-list-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

/* SpeedDial custom styling */
:deep(.p-speeddial) {
  position: relative;
}

:deep(.p-speeddial-list) {
  gap: 0.5rem;
}

.p-card {
  border-radius: 0px;
}

/* Custom button styling for the outlined button */
:deep(.p-button-outlined) {
  border: 1px solid var(--p-primary-color);
  background: transparent;
  color: var(--p-primary-color);
  transition: all 0.2s ease-in-out;
}

:deep(.p-button-outlined:hover) {
  background: var(--p-primary-color);
  color: var(--p-primary-color-text);
  transform: scale(1.05);
}
</style>
